import React from "react";
import { HStack, Button, Text, IconButton } from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";

/**
 * Reusable pagination component following industry best practices
 * 
 * @param {number} currentPage - Current active page (1-indexed)
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Number of items per page
 * @param {function} onPageChange - Callback when page changes
 * @param {boolean} isDisabled - Disable all buttons
 */
const Pagination = ({
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  isDisabled = false,
}) => {
  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Don't show pagination if only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxButtons = 7; // Total page buttons to show (excluding prev/next)

    if (totalPages <= maxButtons) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of middle range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(4, totalPages - 1);
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
        endPage = totalPages - 1;
      }

      // Add ellipsis if gap between first page and start
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis if gap between end and last page
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <HStack spacing={1} justify="center">
      {/* Previous Button */}
      <IconButton
        icon={<ChevronLeftIcon />}
        size="sm"
        variant="ghost"
        onClick={() => onPageChange(currentPage - 1)}
        isDisabled={currentPage === 1 || isDisabled}
        aria-label="Previous page"
      />

      {/* Page Number Buttons */}
      {pageNumbers.map((page, index) => {
        if (page === "...") {
          return (
            <Text key={`ellipsis-${index}`} px={2} fontSize="sm" color="gray.500">
              ...
            </Text>
          );
        }

        return (
          <Button
            key={page}
            size="sm"
            onClick={() => onPageChange(page)}
            colorScheme={currentPage === page ? "blue" : undefined}
            variant={currentPage === page ? "solid" : "ghost"}
            isDisabled={isDisabled}
            minW="40px"
          >
            {page}
          </Button>
        );
      })}

      {/* Next Button */}
      <IconButton
        icon={<ChevronRightIcon />}
        size="sm"
        variant="ghost"
        onClick={() => onPageChange(currentPage + 1)}
        isDisabled={currentPage === totalPages || isDisabled}
        aria-label="Next page"
      />
    </HStack>
  );
};

export default Pagination;

